"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui";

export type NotificationItem = {
  id: string;
  event: string;
  title: string;
  body: string;
  url: string;
  tag?: string | null;
  read_at?: string | null;
  cleared_at?: string | null;
  created_at?: string | null;
  is_read: boolean;
};

export function formatNotificationTs(value?: string | null) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value.replace("T", " ").slice(0, 19);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function NotificationDetailModal({
  item,
  onClose,
  tt,
  hasPrev = false,
  hasNext = false,
  navBusy = false,
  onPrev,
  onNext,
}: {
  item: NotificationItem;
  onClose: () => void;
  tt: (key: string) => string;
  hasPrev?: boolean;
  hasNext?: boolean;
  navBusy?: boolean;
  onPrev?: () => void;
  onNext?: () => void;
}) {
  const [mounted, setMounted] = useState(false);
  const showNav = Boolean(onPrev || onNext);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        ev.preventDefault();
        onClose();
        return;
      }
      if (ev.key === "ArrowLeft" && onPrev) {
        ev.preventDefault();
        onPrev();
        return;
      }
      if (ev.key === "ArrowRight" && onNext) {
        ev.preventDefault();
        onNext();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  if (!mounted) return null;

  return createPortal(
    <div
      className={showNav ? "fixed inset-0 z-[120] flex items-center justify-center px-14 py-4 sm:px-16" : "fixed inset-0 z-[120] flex items-center justify-center p-4"}
      role="dialog"
      aria-modal
    >
      <button type="button" className="absolute inset-0 bg-black/45" aria-label="close" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md">
        {showNav ? (
          <button
            type="button"
            className="msg-nav-chevron msg-nav-chevron--prev"
            disabled={!hasPrev || navBusy}
            aria-label={tt("messagesPrev")}
            onClick={onPrev}
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden className="h-5 w-5">
              <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        ) : null}
        <div className="max-h-[85vh] overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface-solid)] shadow-2xl">
          <div className="flex items-start justify-between gap-3 border-b border-[var(--border)] px-5 py-4">
            <h2 className="text-base font-semibold text-[var(--text)]">{item.title}</h2>
            <button
              type="button"
              className="grid h-8 w-8 place-items-center rounded-full text-[var(--mute)] hover:bg-[var(--nav-hover)]"
              onClick={onClose}
              aria-label="close"
            >
              ×
            </button>
          </div>
          <div className="overflow-y-auto px-5 py-6">
            <div className="whitespace-pre-wrap text-sm leading-relaxed text-[var(--text-body)]">{item.body}</div>
            <p className="mt-6 text-xs text-[var(--mute)]">{formatNotificationTs(item.created_at)}</p>
          </div>
          <div className="flex flex-wrap justify-end gap-2 border-t border-[var(--border)] px-5 py-4">
            {item.url ? (
              <Link href={item.url} onClick={onClose}>
                <Button type="button">{tt("messagesOpenLink")}</Button>
              </Link>
            ) : null}
          </div>
        </div>
        {showNav ? (
          <button
            type="button"
            className="msg-nav-chevron msg-nav-chevron--next"
            disabled={!hasNext || navBusy}
            aria-label={tt("messagesNext")}
            onClick={onNext}
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden className="h-5 w-5">
              <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
